<template>
    <div class="horizontal-tab-navigation" ref="container">
      <div class="tab-reverse" v-if="isTabsOverflow">
        <button type="button" :disabled="!hasPrev" @click="go(-1)">
          <i class="fa fa-chevron-left t-secondary" />
        </button>
      </div>
      <ul class="tab-navigation" ref="list" :class="{ 'w-padding': isTabsOverflow }">
        <li :class="`profile_header_nav_item ${inverted ? 'inverted' : ''}`" v-for="(item, index) in routes" :key="index">
          <router-link v-if="item.visibility" :to="getRoute(item)" :class="classObject(item)" :title="item.meta.render.tooltip">
            <span class="t-600 t-md">{{ item.meta.render.title }}</span>
            <span v-if="counts && getCount(item.meta.render) && item.visibility" class="t-sm pl-1">
              ({{ getCount(item.meta.render).total }})
            </span>
            <a v-if="!item.visibility" href="#" @click.prevent="openModal()" :class="classObject(item)">
              <span v-if="item.meta.render.title && text">{{ item.meta.render.title }}</span>
            </a>
          </router-link>
        </li>
      </ul>
      <div class="tab-forward" v-if="isTabsOverflow">
        <button type="button" :disabled="isScrolledToEnd" @click="go(1)">
          <i class="fa fa-chevron-right t-secondary" />
        </button>
      </div>
    </div>
</template>

<script>
export default {
  name: 'subNav',
  data () {
    return {
      isScrolledToEnd: false,
      stepPosition: 0,
      width: {
        container: 0,
        scrollWidth: 1
      },
      isTabsOverflow: false,
      offset: 20
    }
  },
  props: {
    rw_access: {
      type: String,
      default: ''
    },
    rw_pages: {
      type: Object,
      default: () => ({}),
      required: false
    },
    text: {
      type: Boolean,
      default: false
    },
    icon: {
      type: Boolean,
      default: false
    },
    inverted: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      default: ''
    },
    items: {
      type: Array,
      default: () => []
    },
    counts: {
      type: Object,
      default: () => {}
    },
    user: {
      type: Object,
      default: () => ({})
    },
    researcher: {
      type: Object,
      default: () => ({})
    },
    organisation: {
      type: Object,
      default: () => ({})
    },
    community: {
      type: Object,
      default: () => ({})
    },
    hasResearch: {
      type: Boolean,
      default: true
    },
    hasPortfolio: {
      type: Boolean,
      default: false,
      required: false
    },
    hasNews: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  mounted () {
    window.addEventListener('resize', this.screenResizeAction)
    this.screenResizeAction()
  },
  computed: {
    workingWidth () {
      return this.width.container - this.offset
    },
    hasPrev () {
      return this.stepPosition > 0
    },
    routes () {
      let routes = []
      let main = this.$route.matched[0].name
      let parentRoute = this.$route.matched[1].name

      this.$router.options.routes.find((el) => {
        return el.name === main
      }).children.find((el) => {
        return el.name === parentRoute
      }).children.filter((el) => {
        if (!this.isEmpty(el.meta)) {
          el.meta.visibility
            ? el.visibility = (this.rw_access === 'public') ? this.rw_pages[el.meta.visibility.key] : true
            : el.visibility = true

          let hide = false

          // Override "Research" tab title to "Portfolio" for incubator organisations or back if it had been changed.
          if (el.meta.render.title.includes('Portfolio')) {
            el.meta.render.title = 'Research'
          }
          if (this.name === 'organisation_header_nav') {
            if (el.meta.render && el.meta.render.checkForPortfolio && this.hasPortfolio) {
              if (this.hasNews) {
                el.meta.render.title = 'Portfolio & News'
              } else {
                el.meta.render.title = 'Portfolio'
              }
            }
          }

          if (el.meta.render && el.meta.render.checkForRenderInSubNavExplore && !this.hasResearch) hide = true

          // check if we need to render nav item in community
          if (this.name === 'community_header_nav') {
            // hide workspace in all types except project consortium
            if (el.meta.render.checkForRenderInSubNavWorkspace && this.community.community_type_id !== 2) hide = true
          }

          // check if we need to render nav item in researcher
          if (this.name === 'researcher_header_nav') {
            if (el.meta.render.checkForRenderInSubNavList && !this.user.id) hide = true
            if (el.meta.render.checkForRenderInSubNavList && this.user.id && !this.user.bookmark_folder_count && this.rw_access === 'public') hide = true
          }

          // push routes that the user can see
          if (el.meta.render && this.$can.seeUrl(el.meta.permissions, parentRoute, this.rw_access) && !hide) routes.push(el)
        }
      })
      return routes
    }
  },
  methods: {
    getRoute (item) {
      const route = { name: item.name, params: this.getParams(item) }
      if (item.meta.setGlobalKeyword) {
        route.query = { keyword: this.globalSearchKeyword }
      }
      return route
    },
    screenResizeAction () {
      const listElement = this.$refs.list
      // Update local variables
      this.width.container = this.$refs.container.clientWidth
      this.width.scrollWidth = listElement.scrollWidth
      // logic to add or remove padding to last child element when overflow
      this.isTabsOverflow = listElement.scrollWidth > listElement.clientWidth
    },
    go (direction) {
      this.stepPosition += direction
      let scrollLeft = this.isScrolledToEnd
        ? this.width.scrollWidth - (this.workingWidth * 2)
        : this.workingWidth * this.stepPosition

      if (scrollLeft + this.workingWidth > (this.width.scrollWidth - this.offset)) {
        this.isScrolledToEnd = true
        scrollLeft = this.width.scrollWidth
        this.stepPosition -= 0
      } else {
        this.isScrolledToEnd = false
      }

      this.$refs.list.scrollTo({ top: 0, left: scrollLeft, behavior: 'smooth' })
    },
    getParams (item) {
      return (~item.path.indexOf(':id')) ? { id: this.$route.params.id } : {}
    },
    getCount (route) {
      let title = route.count ? route.count : route.title
      return this.counts[title.replace(/ /g, '_').toLowerCase()]
    },
    classObject (item) {
      let active = false
      if (item.meta.render.children) active = item.meta.render.children.includes(this.$route.name)

      return (item.name === this.$route.name || active)
        ? `active`
        : ``
    },
    openModal () {
      !this.$currentUser
        ? this.$events.$emit('modal:login:open')
        : this.$events.$emit('modal:invite_request:open', this.$route.params.id, this.$route.name.split('.')[0])
    }
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.screenResizeAction)
  }
}
</script>
